import React from 'react';
import { Permission, UserRole } from '../../types/user.types';

interface PermissionsSelectorProps {
  selectedPermissions: Permission[];
  onChange: (permissions: Permission[]) => void;
  disabled?: boolean;
  role?: UserRole;
}

const ACTIVE_PERMS: Permission[] = ['approve_customers', 'approve_suppliers', 'manage_users'];

const PERMISSIONS: { value: Permission; label: string; description: string }[] = [
  {
    value: 'approve_customers',
    label: 'Approve Customers',
    description: 'Can approve or reject customer registrations',
  },
  {
    value: 'approve_suppliers',
    label: 'Approve Suppliers',
    description: 'Can approve or reject supplier registrations',
  },
  {
    value: 'manage_users',
    label: 'Manage Users',
    description: 'Can create, edit, and delete users',
  },
  {
    value: 'view_only',
    label: 'View Only',
    description: 'Can only view registrations without approval rights',
  },
];

export const PermissionsSelector: React.FC<PermissionsSelectorProps> = ({
  selectedPermissions,
  onChange,
  disabled = false,
  role: _role,
}) => {
  const isDisabledByExclusion = (permission: Permission): boolean => {
    if (permission === 'view_only') {
      return selectedPermissions.some(p => ACTIVE_PERMS.includes(p));
    }
    if (ACTIVE_PERMS.includes(permission)) {
      return selectedPermissions.includes('view_only');
    }
    return false;
  };

  const togglePermission = (permission: Permission) => {
    if (disabled || isDisabledByExclusion(permission)) return;

    if (permission === 'view_only') {
      if (selectedPermissions.includes('view_only')) {
        onChange([]);
      } else {
        onChange(['view_only']);
      }
      return;
    }

    // Active permission selected — remove view_only if present
    if (selectedPermissions.includes(permission)) {
      onChange(selectedPermissions.filter(p => p !== permission));
    } else {
      onChange([...selectedPermissions.filter(p => p !== 'view_only'), permission]);
    }
  };

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Permissions
      </label>
      {PERMISSIONS.map(permission => {
        const isExcluded = isDisabledByExclusion(permission.value);
        return (
          <div
            key={permission.value}
            className={`border rounded-lg p-4 transition-all ${
              selectedPermissions.includes(permission.value)
                ? 'border-accent-500 bg-accent-50'
                : 'border-gray-300 hover:border-gray-400'
            } ${disabled || isExcluded ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            onClick={() => togglePermission(permission.value)}
          >
            <div className="flex items-start">
              <input
                type="checkbox"
                checked={selectedPermissions.includes(permission.value)}
                onChange={() => togglePermission(permission.value)}
                disabled={disabled || isExcluded}
                className="mt-1 h-4 w-4 text-accent-600 focus:ring-accent-500 border-gray-300 rounded"
              />
              <div className="ml-3">
                <div className="text-sm font-medium text-gray-900">
                  {permission.label}
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  {permission.description}
                </div>
                {isExcluded && (
                  <div className="text-xs text-amber-600 mt-1">
                    {permission.value === 'view_only'
                      ? 'Cannot combine with active permissions'
                      : 'Cannot combine with View Only'}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
